/**
 * Per-screen TTID / TTFD tracking (AXI-3875).
 *
 * The router is element-based, so there is no loader to time against. Instead
 * the route telemetry layer drives this tracker:
 *
 *   • `markNavigationStart(pathname)` — fired (during render) on every route
 *     change. Captures the navigation start, opens a navigation trace so the
 *     screen's requests share one trace id, and arms the cap timer.
 *   • `markFirstRender()` — fired from a post-paint rAF; records TTID
 *     (navigation start → first paint of the new route).
 *   • `onQueryStart()` / `onQuerySettle()` — fed by the RTK query-inflight
 *     middleware; TTFD is the moment the screen's in-flight queries drain back
 *     to zero (debounced so chained queries don't end the window early).
 *
 * Resolution rules, in priority order:
 *   1. data settled  → TTFD = navigation start → last query settled.
 *   2. data-less route (no query within a grace window after TTID) → TTFD = TTID.
 *   3. hard cap / interrupted by the next navigation → TTFD = null, flagged.
 *
 * Everything is best-effort and browser-guarded; in a non-DOM context the
 * functions are inert.
 */
import {
  beginNavigationTrace,
  endNavigationTrace,
  type NavigationTrace
} from '../api/core/traceparent'
import { enqueueBeacon } from './collector-client'
import { routeLabelFromPath } from './route-label'

/** Absolute ceiling on a load window — polling/never-settling screens stop here. */
const HARD_CAP_MS = 15_000
/** Wait after in-flight drops to zero, in case the screen chains another query. */
const SETTLE_DEBOUNCE_MS = 100
/** After first paint, if no query has started, treat the route as data-less. */
const DATA_LESS_GRACE_MS = 500

interface PendingNavigation {
  route: string
  navType: 'initial' | 'spa'
  startMs: number
  ttidMs: number | null
  trace: NavigationTrace
  seenQuery: boolean
  inflight: number
  queryCount: number
  finalized: boolean
  capTimer: ReturnType<typeof setTimeout> | null
  settleTimer: ReturnType<typeof setTimeout> | null
  graceTimer: ReturnType<typeof setTimeout> | null
}

let pending: PendingNavigation | null = null
let firstNavigation = true
let currentRouteLabel = '/'

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : 0
}

function clearTimers(nav: PendingNavigation): void {
  if (nav.capTimer) clearTimeout(nav.capTimer)
  if (nav.settleTimer) clearTimeout(nav.settleTimer)
  if (nav.graceTimer) clearTimeout(nav.graceTimer)
  nav.capTimer = nav.settleTimer = nav.graceTimer = null
}

function traceparentOf(trace: NavigationTrace): string {
  return `00-${trace.traceId}-${trace.rootSpanId}-01`
}

/**
 * Close the current load window and emit its navigation beacon. `ttfdMs` is
 * null when the window never cleanly resolved (cap fired / interrupted), in
 * which case `capped` is true so dashboards can exclude it.
 */
function finalize(ttfdMs: number | null, capped: boolean): void {
  if (!pending || pending.finalized) return
  const nav = pending
  nav.finalized = true
  clearTimers(nav)
  pending = null

  // A clean TTID is preferred; fall back to the TTFD (or elapsed) only when the
  // window ended before first paint was recorded (rare — capped/interrupted).
  const ttidMs = nav.ttidMs ?? ttfdMs ?? now() - nav.startMs

  enqueueBeacon({
    kind: 'navigation',
    route: nav.route,
    navType: nav.navType,
    ttidMs,
    ttfdMs,
    queryCount: nav.queryCount,
    cappedTtfd: capped,
    traceparent: traceparentOf(nav.trace)
  })

  endNavigationTrace()
}

/** Begin timing a new screen. Idempotent per pathname — call on route change only. */
export function markNavigationStart(pathname: string): void {
  // A new navigation before the previous one resolved means the user moved on;
  // emit the previous as interrupted (TTFD unmeasured) rather than dropping it.
  if (pending && !pending.finalized) {
    finalize(null, true)
  }

  const route = routeLabelFromPath(pathname)
  const navType: 'initial' | 'spa' = firstNavigation ? 'initial' : 'spa'
  firstNavigation = false
  currentRouteLabel = route

  // For the initial document load, measure from the navigation/timeOrigin
  // (performance.now() === ms since timeOrigin), so TTID/TTFD include the time
  // the bundle took to boot. SPA navigations measure from this render.
  const startMs = navType === 'initial' ? 0 : now()

  const nav: PendingNavigation = {
    route,
    navType,
    startMs,
    ttidMs: null,
    trace: beginNavigationTrace(),
    seenQuery: false,
    inflight: 0,
    queryCount: 0,
    finalized: false,
    capTimer: null,
    settleTimer: null,
    graceTimer: null
  }
  pending = nav

  const capRemaining = Math.max(0, HARD_CAP_MS - (now() - startMs))
  nav.capTimer = setTimeout(() => finalize(null, true), capRemaining)
}

/** Record TTID (first paint of the route). Fired from a post-commit rAF. */
export function markFirstRender(): void {
  if (!pending || pending.ttidMs !== null) return
  pending.ttidMs = now() - pending.startMs

  // If no data query has started by first paint, the screen is static —
  // resolve TTFD to TTID after a short grace (a query may still arrive).
  if (!pending.seenQuery) {
    pending.graceTimer = setTimeout(() => {
      if (pending && !pending.seenQuery && pending.ttidMs !== null) {
        finalize(pending.ttidMs, false)
      }
    }, DATA_LESS_GRACE_MS)
  }
}

/** One RTK query entered flight during the active load window. */
export function onQueryStart(): void {
  if (!pending) return
  pending.seenQuery = true
  pending.inflight += 1
  pending.queryCount += 1
  // Data is on the way: cancel the data-less grace and any pending settle.
  if (pending.graceTimer) {
    clearTimeout(pending.graceTimer)
    pending.graceTimer = null
  }
  if (pending.settleTimer) {
    clearTimeout(pending.settleTimer)
    pending.settleTimer = null
  }
}

/** One RTK query settled (fulfilled or rejected) during the load window. */
export function onQuerySettle(): void {
  if (!pending) return
  pending.inflight = Math.max(0, pending.inflight - 1)
  if (pending.inflight > 0 || !pending.seenQuery) return
  // In-flight drained to zero — TTFD candidate. Debounce so a chained query
  // (a list load that triggers detail loads) keeps the window open.
  if (pending.settleTimer) clearTimeout(pending.settleTimer)
  pending.settleTimer = setTimeout(() => {
    if (pending && pending.inflight === 0 && pending.seenQuery) {
      finalize(now() - pending.startMs, false)
    }
  }, SETTLE_DEBOUNCE_MS)
}

/**
 * Wrap one non-RTK data fetch (Connect RPC, custom `fetch` hook) so it feeds
 * TTFD the same way RTK queries do via the in-flight middleware. The Tags page,
 * for example, loads through ConnectRPC + bespoke `useQuery` hooks rather than
 * RTK, and would otherwise be misread as data-less. Resolves/rejects exactly as
 * the wrapped promise does.
 */
export function trackDataFlight<T>(promise: Promise<T>): Promise<T> {
  onQueryStart()
  return promise.finally(() => onQuerySettle())
}

/** The route label of the screen currently mounted — used to tag Web Vitals. */
export function getCurrentRouteLabel(): string {
  return currentRouteLabel
}

/** Test seam: forget all navigation state and timers. */
export function __resetNavigationTimingForTests(): void {
  if (pending) clearTimers(pending)
  pending = null
  firstNavigation = true
  currentRouteLabel = '/'
  endNavigationTrace()
}
